import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { Breadcrumb } from '@/components/Breadcrumb.tsx'
import { StyledActionButton } from '@/components/Buttons.tsx'
import { Stepper } from '@/components/Stepper.tsx'
import {
  ButtonGroup,
  FullScreenContainer,
  ScrollableContent,
} from '@/components/templates/ScreenLayout.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { TimeSlots } from '@/components/templates/TimeSlots.tsx'
import { BodyM, Header1 } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress'
import {
  confirmBookingPath,
  selectDateTimePath,
  selectDoctorPath,
} from '@/router/paths.ts'
import { useAppointmentStore } from '@/store.ts'
import { VelthuisFormStep } from '@/utils/VelthuisFormSteps.ts'

import * as S from './SelectDateTime.styled'

const SelectDateTime = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'selectDateTime',
  })
  const navigate = useNavigate()
  const { appointmentType, category, doctorId, locationId } = useParams() as {
    appointmentType: string
    category: string
    doctorId: string
    locationId: string
  }
  const { getDoctorsByLocation, getSlotsByDoctor, selectedSlot, sortedSlots } =
    useAppointmentStore((state) => ({
      getDoctorsByLocation: state.getDoctorsByLocation,
      getSlotsByDoctor: state.getSlotsByDoctor,
      selectedSlot: state.selectedSlot,
      sortedSlots: state.sortedSlots,
    }))

  const { trackFormError, trackFormInteraction } = useTrackFormProgress(
    VelthuisFormStep.SelectDateTime,
  )

  const slots =
    doctorId && doctorId !== 'all' ? getSlotsByDoctor(doctorId) : sortedSlots

  useEffect(() => {
    if (!slots || slots.length === 0) {
      trackFormError({
        error: t('noSlots'),
      })
    }
  }, [slots])

  return (
    <ScreenWrapper>
      <FullScreenContainer>
        <Breadcrumb
          onClick={() => {
            navigate(
              selectDoctorPath({
                appointmentType,
                category,
                locationId,
              }),
            )
            trackFormInteraction({
              action: 'previous',
              formFieldName: 'back-to-previous',
            })
          }}
          title={t('breadcrumbLink')}
        />
        <Header1>{t('header')}</Header1>
        <Stepper total={6} step={4} />
        <S.Description>{t('description')}</S.Description>
        <ScrollableContent>
          {slots && slots?.length > 0 && (
            <TimeSlots
              slots={slots}
              onSlotClick={(slot) => {
                trackFormInteraction({
                  action: 'click',
                  formFieldName: 'timeSlot',
                  formFieldValue: `${slot.start} - ${slot.end}`,
                })
              }}
            />
          )}

          {slots?.length === 0 && <BodyM>{t('noSlots')}</BodyM>}
        </ScrollableContent>

        <ButtonGroup>
          <StyledActionButton
            variant="Primary"
            type="submit"
            onClick={() => {
              navigate(
                confirmBookingPath({
                  appointmentType,
                  category,
                  doctorId,
                  locationId,
                }),
              )
              trackFormInteraction({
                action: 'next',
                formFieldName: 'submit',
              })
            }}
            disabled={!selectedSlot}
          >
            {t('actions.reviewBooking')}
          </StyledActionButton>
          {doctorId !== 'all' && getDoctorsByLocation().length > 1 && (
            <StyledActionButton
              variant="Secondary"
              onClick={() => {
                trackFormInteraction({
                  action: 'click',
                  formFieldName: 'showAllSlots',
                })
                navigate(
                  selectDateTimePath({
                    appointmentType,
                    category,
                    doctorId: 'all',
                    locationId,
                  }),
                )
              }}
            >
              {t('actions.showAllClinicianSlots')}
            </StyledActionButton>
          )}
        </ButtonGroup>
      </FullScreenContainer>
    </ScreenWrapper>
  )
}

export { SelectDateTime }
